import { useCallback, useEffect, useMemo } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showError } from "../../../../../redux/common/actions";
import { getModelTaskPage } from "services/models/modelDetails/modelTasks";

export const PAGE_SIZE = 50;

const normaliseTask = (task) => ({
	...task,
	hasTaskImages: task.hasTaskImages,
	hasQuestion: task.hasQuestions,
	errors: task.hasErrors,
	systemName: task.systemName ?? "",
});

const flattenPages = (pages) => {
	const tasks = new Map();
	[...(pages ?? [])]
		.sort((left, right) => left.pageNumber - right.pageNumber)
		.forEach((page) => {
			page.items.forEach((task) => tasks.set(task.id, normaliseTask(task)));
		});
	return [...tasks.values()];
};

// Typed text only drives autocomplete suggestions, never this query directly —
// committing it as a "Contains" chip is what applies it, so the list doesn't
// refetch (and disrupt the open suggestion dropdown) on every keystroke.
const useTaskListQuery = ({ modelId, sortField, sortDesc, chipParams, navigationAnchorId }) => {
	const dispatch = useDispatch();
	const queryClient = useQueryClient();

	const queryKey = useMemo(
		() => [
			"modelTasks",
			modelId,
			chipParams,
			sortField,
			sortDesc ? "desc" : "asc",
			PAGE_SIZE,
			navigationAnchorId ?? null,
		],
		[chipParams, modelId, navigationAnchorId, sortDesc, sortField]
	);

	const query = useInfiniteQuery({
		queryKey,
		enabled: Boolean(modelId),
		initialPageParam: { pageNumber: 1, anchorTaskId: navigationAnchorId },
		queryFn: async ({ pageParam, signal }) => {
			const response = await getModelTaskPage({
				modelVersionId: modelId,
				pageNumber: pageParam.pageNumber,
				pageSize: PAGE_SIZE,
				sortField,
				sortOrder: sortDesc ? "desc" : "asc",
				anchorTaskId: pageParam.anchorTaskId,
				...chipParams,
				signal,
			});
			if (!response?.status) {
				throw new Error(response?.data?.detail || "Failed to fetch task list");
			}
			return response.data;
		},
		getNextPageParam: (lastPage) =>
			lastPage.hasNextPage ? { pageNumber: lastPage.pageNumber + 1 } : undefined,
		getPreviousPageParam: (firstPage) =>
			firstPage.hasPreviousPage ? { pageNumber: firstPage.pageNumber - 1 } : undefined,
	});

	useEffect(() => {
		if (query.isError) dispatch(showError(query.error?.message));
	}, [dispatch, query.error, query.isError]);

	const refresh = useCallback(
		() => queryClient.invalidateQueries({ queryKey: ["modelTasks", modelId] }),
		[modelId, queryClient]
	);

	const pages = query.data?.pages;
	return {
		rows: useMemo(() => flattenPages(pages), [pages]),
		totalCount: pages?.[0]?.totalCount ?? 0,
		refresh,
		query,
	};
};

export default useTaskListQuery;
